import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { Page } from '../app/AppShell.js';
import { useToast } from '../components/Toast.js';
import { Loading, ErrorNote } from '../components/ui.js';
import type { MenuCategoryDraft, MenuProductDraft } from '../lib/types.js';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'IDR', 'AUD', 'CAD', 'JPY', 'SGD'];

function blankProduct(): MenuProductDraft {
  return { name: '', description: null, price: null, available: true };
}

function blankCategory(): MenuCategoryDraft {
  return { name: 'New category', products: [] };
}

export function MenuBoardEditorScreen(): ReactNode {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const board = useQuery(() => api.menuBoard(id!), [id]);

  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [categories, setCategories] = useState<MenuCategoryDraft[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!board.data) return;
    setName(board.data.name);
    setCurrency(board.data.currency);
    setCategories(
      board.data.categories.map((c) => ({
        name: c.name,
        products: c.products.map((p) => ({
          name: p.name,
          description: p.description,
          price: p.price,
          available: p.available,
        })),
      })),
    );
  }, [board.data]);

  if (board.loading && !board.data) return <Loading what="menu board" />;
  if (board.error) return <ErrorNote error={board.error} />;
  if (categories === null) return <Loading />;

  function patchCategory(ci: number, next: Partial<MenuCategoryDraft>): void {
    setCategories((cats) => cats?.map((c, i) => (i === ci ? { ...c, ...next } : c)) ?? cats);
  }

  function patchProduct(ci: number, pi: number, next: Partial<MenuProductDraft>): void {
    setCategories(
      (cats) =>
        cats?.map((c, i) =>
          i === ci ? { ...c, products: c.products.map((p, j) => (j === pi ? { ...p, ...next } : p)) } : c,
        ) ?? cats,
    );
  }

  async function save(): Promise<void> {
    if (!id || !categories) return;
    setSaving(true);
    try {
      await api.updateMenuBoard(id, { name: name.trim() || 'Untitled menu', currency, categories });
      toast.push('Saved', 'success');
      board.refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? new Error(err.fieldMessage() ?? err.message) : err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title="Edit menu board"
      actions={
        <>
          <button className="btn" onClick={() => navigate('/menu-boards')}>
            Back
          </button>
          <button className="btn primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ maxWidth: 760 }}>
        <div className="card card-pad row wrap">
          <label className="field" style={{ flex: 2, minWidth: 220 }}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field" style={{ width: 120 }}>
            Currency
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {(CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES]).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        {categories.map((category, ci) => (
          <div key={ci} className="region-block stack">
            <div className="row between">
              <input
                value={category.name}
                placeholder="Category name"
                onChange={(e) => patchCategory(ci, { name: e.target.value })}
                style={{ fontWeight: 600, maxWidth: 260 }}
              />
              <button
                className="btn ghost small danger"
                onClick={() => setCategories((cats) => cats?.filter((_, i) => i !== ci) ?? cats)}
              >
                Remove category
              </button>
            </div>

            {category.products.length === 0 ? (
              <p className="faint small" style={{ margin: 0 }}>
                No products yet.
              </p>
            ) : (
              category.products.map((product, pi) => (
                <div key={pi} className="widget-row product-row">
                  <input
                    value={product.name}
                    placeholder="Product name"
                    onChange={(e) => patchProduct(ci, pi, { name: e.target.value })}
                  />
                  <input
                    value={product.description ?? ''}
                    placeholder="Description (optional)"
                    onChange={(e) => patchProduct(ci, pi, { description: e.target.value || null })}
                  />
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={product.price ?? ''}
                    placeholder="Price"
                    onChange={(e) =>
                      patchProduct(ci, pi, { price: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                  <label className="row small" style={{ alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={product.available}
                      onChange={(e) => patchProduct(ci, pi, { available: e.target.checked })}
                    />
                    Available
                  </label>
                  <button
                    className="btn ghost small danger"
                    onClick={() =>
                      patchCategory(ci, { products: category.products.filter((_, j) => j !== pi) })
                    }
                  >
                    ✕
                  </button>
                </div>
              ))
            )}

            <button
              className="btn small"
              onClick={() => patchCategory(ci, { products: [...category.products, blankProduct()] })}
            >
              + Add product
            </button>
          </div>
        ))}

        <button className="btn" onClick={() => setCategories((cats) => [...(cats ?? []), blankCategory()])}>
          Add category
        </button>
      </div>
    </Page>
  );
}
